"use client";

import React from "react";
import { MapPin, Navigation, Compass, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const regions = [{
  id: 1,
  regionName: "Valle del Guadalentín",
  description: "Asistencia rápida y servicio prioritario con sede central en Totana y municipios vecinos.",
  badgeText: "Sede Central & Urgencias"
}, {
  id: 2,
  regionName: "Costa Cálida & Almería",
  description: "Atención especializada para viviendas residenciales, chalets y zonas turísticas de costa.",
  badgeText: "Servicio Costa"
}, {
  id: 3,
  regionName: "Área Metropolitana & Alicante",
  description: "Cobertura integral para comunidades de vecinos, locales comerciales y zonas urbanas.",
  badgeText: "Comunidades & Empresas"
}];
const towns = [{
  id: 101,
  regionId: 1,
  name: "Totana",
  url: "/TotanaPage",
  isPrimary: true
}, {
  id: 102,
  regionId: 1,
  name: "Lorca",
  url: "#",
  isPrimary: false
}, {
  id: 103,
  regionId: 1,
  name: "Alhama de Murcia",
  url: "#",
  isPrimary: false
}, {
  id: 104,
  regionId: 1,
  name: "Librilla",
  url: "#",
  isPrimary: false
}, {
  id: 105,
  regionId: 1,
  name: "Puerto Lumbreras",
  url: "#",
  isPrimary: false
}, {
  id: 201,
  regionId: 2,
  name: "Mazarrón",
  url: "#",
  isPrimary: false
}, {
  id: 202,
  regionId: 2,
  name: "Águilas",
  url: "#",
  isPrimary: false
}, {
  id: 203,
  regionId: 2,
  name: "Vera",
  url: "#",
  isPrimary: false
}, {
  id: 204,
  regionId: 2,
  name: "Mojácar",
  url: "#",
  isPrimary: false
}, {
  id: 205,
  regionId: 2,
  name: "Almería",
  url: "/AlmeriaPage",
  isPrimary: true
}, {
  id: 301,
  regionId: 3,
  name: "Murcia Capital",
  url: "/MurciaPage",
  isPrimary: true
}, {
  id: 302,
  regionId: 3,
  name: "Alcantarilla",
  url: "#",
  isPrimary: false
}, {
  id: 303,
  regionId: 3,
  name: "Orihuela",
  url: "#",
  isPrimary: false
}, {
  id: 304,
  regionId: 3,
  name: "Torrevieja",
  url: "#",
  isPrimary: false
}, {
  id: 305,
  regionId: 3,
  name: "Alicante",
  url: "/AlicantePage",
  isPrimary: true
}];
export default function Coberturasureste() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  return <section id="coberturasureste" ref={ref} className={`relative w-full bg-background py-20 lg:py-28 transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center space-y-4 mb-16">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-[var(--chart-1)]">
            ÁREA DE COBERTURA TÉCNICA
          </span>
          <h2 className="font-default font-mono text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground uppercase">
            Áreas que Atendemos en el Sureste
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Ofrecemos servicios técnicos profesionales de fontanería, detección de fugas, piscinas y climatización en Totana, Murcia, Alicante y Almería.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {regions.map((region, i) => {
          const regionTowns = towns.filter(t => t.regionId === region.id);
          return <Card key={region.id} data-index={i} className="border border-border/70 bg-card shadow-md hover:shadow-lg transition-all duration-300 flex flex-col justify-between">
                <CardHeader className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center justify-center size-12 rounded-lg bg-[var(--chart-1)]/10 text-[var(--chart-1)]">
                      {i === 0 && <MapPin className="size-6" />}
                      {i === 1 && <Compass className="size-6" />}
                      {i === 2 && <Navigation className="size-6" />}
                    </div>
                    <Badge variant="outline" className="border-[var(--chart-1)]/30 text-xs text-foreground bg-background">
                      {region.badgeText}
                    </Badge>
                  </div>
                  <div>
                    <CardTitle className="font-default text-xl font-bold text-card-foreground mb-2">
                      {region.regionName}
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground font-sans">
                      {region.description}
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Localidades Principales:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {regionTowns.map((town, townIdx) => <div key={town.id} data-index={townIdx}>
                        {town.url !== "#" ? <Link to={town.url} className="inline-block">
                            <Badge variant={town.isPrimary ? "default" : "secondary"} className={`px-3 py-1 text-xs cursor-pointer transition-colors ${town.isPrimary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"}`}>
                              {town.name}
                              <ArrowRight className="size-3 ml-1" />
                            </Badge>
                          </Link> : <Badge variant="secondary" className="px-3 py-1 text-xs bg-muted/60 text-muted-foreground">
                            {town.name}
                          </Badge>}
                      </div>)}
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="bg-card border border-border rounded-xl p-8 sm:p-10 text-center max-w-4xl mx-auto shadow-md space-y-6">
          <div className="space-y-2">
            <h3 className="font-default text-2xl font-semibold text-foreground">
              ¿No encuentras tu localidad o necesitas atención inmediata?
            </h3>
            <p className="text-muted-foreground font-sans text-sm sm:text-base max-w-xl mx-auto">
              Respondemos al instante por WhatsApp para resolver dudas sobre servicios de fontanería, detección de fugas o presupuestos en toda la región.
            </p>
          </div>

          <div className="flex justify-center pt-2">
            <Button size="lg" className="gap-2 text-base px-6 py-6" asChild>
              <Link to="https://wa.me/34657068444?text=Hola%2C%20necesito%20informaci%C3%B3n%20sobre%20vuestros%20servicios" newTab>
                <MessageCircle className="size-5 text-emerald-400 fill-emerald-400/20" />
                <span>Contactar por WhatsApp</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}